import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import MarketingNav from '../components/MarketingNav';
import MarketingFooter from '../components/MarketingFooter';
import SEO from '../components/SEO';
import { ArrowRight, TrendingUp, Target, Clock, Quote, BarChart3 } from 'lucide-react';

const CASES = [
  {
    industry: 'Performance-Marketing-Agentur',
    title: 'Vom Cold-Email-Sumpf zu planbaren Erstgesprächen',
    challenge: 'Eine inhabergeführte Agentur hatte ihren Outbound stark heruntergefahren – Massenmails brachten kaum Termine, die Recherche kostete pro Lead 20+ Minuten. Klassische Adresslisten waren veraltet und lieferten zu viele unpassende Kontakte – egal ob deutschlandweit oder regional gesucht wurde.',
    approach: 'Mit LaizyLeads beschrieben sie ihren ICP per Voice. Unser performance-optimierter Leadgenerator lieferte täglich neue, hochwertige Leads – jeweils mit Begründung, Score und fertigem Aufhänger. Sowohl bundesweit als auch regional gefiltert lieferte das System konstant qualitativ gute Treffer.',
    result: [
      { kpi: '+62%', label: 'mehr qualifizierte Termine in 90 Tagen' },
      { kpi: '−78%', label: 'weniger Recherchezeit pro Lead' },
      { kpi: '3,4x', label: 'höhere Antwortquote durch personalisierten Outreach' },
    ],
    quote: 'Es bringt uns in der Neukundengewinnung massiv weiter und wir können Umsatz erzielen.',
  },
  {
    industry: 'B2B-SaaS (HR-Tech)',
    title: 'Bessere Zielkunden identifizieren statt Massenoutreach',
    challenge: 'Ein SaaS-Anbieter wollte den Outbound auf mittelständische Unternehmen mit klar definiertem ICP fokussieren. Bestehende Datenbanken lieferten zu viele Zu-Kleine und Zu-Große – mit hohen monatlichen Fixkosten ohne planbaren ROI.',
    approach: 'Mit LaizyLeads steuerten sie die Suche flexibel zwischen deutschlandweit und einzelnen Bundesländern. Die KI bewertete jeden Lead auf Passung, Bedarf und Erreichbarkeit – die Ergebnisse waren in beiden Modi konstant hochwertig und aktuell.',
    result: [
      { kpi: '+41%', label: 'Steigerung der Pipeline-Qualität (gemessen an SQL-Rate)' },
      { kpi: '5h', label: 'gesparte Sales-Zeit pro Woche und Mitarbeiter' },
      { kpi: '+18%', label: 'Umsatzwachstum im ersten Halbjahr' },
    ],
    quote: 'Mit dem Preis hat sich das Ganze mit einem oder zwei Neukunden schon gelohnt.',
  },
  {
    industry: 'Industriedienstleister',
    title: 'Mehr Umsatz durch bessere Leads – deutschlandweit & in der Nische',
    challenge: 'Ein technischer Dienstleister hatte einen sehr konkreten Zielmarkt – aber keine Datenquelle, die diese Nische zuverlässig abdeckte. Klassische Listen waren oft Karteileichen oder enthielten Fake-Firmen, die längst nicht mehr existierten.',
    approach: 'Unser Leadgenerator erschloss die Nische über aktuelle, performante Web-Daten – sowohl in der gewünschten Region als auch deutschlandweit. Jeder Lead wurde mit Standort-Verifizierung über Impressum-Abgleich geprüft – keine Fake-Firmen, keine veralteten Adressen.',
    result: [
      { kpi: '47', label: 'neue Zielkunden im ersten Quartal entdeckt' },
      { kpi: '+22%', label: 'Auftragseingang aus neuen B2B-Kontakten' },
      { kpi: '0', label: 'Streuverluste – jeder Lead war geographisch korrekt' },
    ],
    quote: 'Wir haben endlich einen Leadgenerator, der uns Leads anzeigt, die wirklich zu uns passen – und nicht irgendwelche Fake-Firmen.',
  },
];

export default function CaseStudiesPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-white text-gray-900" data-testid="case-studies-page">
      <SEO
        title="Case Studies – Mehr Umsatz durch bessere Leads | LaizyLeads"
        description="Echte Ergebnisse aus B2B-Vertriebsteams: Wie Agenturen, SaaS-Anbieter und Industriedienstleister mit dem LaizyLeads Leadgenerator hochwertige Leads gewinnen und Umsatzwachstum erzielen."
        path="/case-studies"
        keywords="mehr Umsatz durch bessere Leads, hochwertige Leads, B2B Neukunden gewinnen, Case Study Leadgenerierung, Erfolgsgeschichten B2B"
      />
      <MarketingNav />

      {/* HERO */}
      <section className="relative pt-28 pb-16 sm:pt-32 sm:pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 via-white to-gray-50" />
        <div className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8 text-center">
          <Badge className="mb-6 bg-emerald-100 text-emerald-700 border-emerald-200 px-3.5 py-1.5 text-sm font-medium">
            <BarChart3 className="w-3.5 h-3.5 mr-1.5" /> Case Studies
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] mb-6">
            Mehr Umsatz durch<span className="block text-emerald-600">bessere Leads.</span>
          </h1>
          <p className="text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto mb-2">
            Wir haben einen Leadgenerator mit perfekter Performance gebaut – egal ob deutschlandweit oder regional gesucht wird, das Ergebnis sind hochwertige Leads, die wirklich passen. Drei Vertriebsteams aus drei Branchen zeigen, was damit möglich ist.
          </p>
          <p className="text-sm text-gray-500 max-w-2xl mx-auto">
            Hinweis: Aus Datenschutzgründen sind sämtliche Kundendetails anonymisiert. Referenzen nennen wir gerne im persönlichen Gespräch.
          </p>
        </div>
      </section>

      {/* CASES */}
      <section className="py-12 bg-white">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 space-y-12">
          {CASES.map((c, i) => (
            <article key={i} className="rounded-2xl border border-gray-200 overflow-hidden shadow-sm" data-testid={`case-${i}`}>
              <div className="bg-emerald-50 border-b border-emerald-100 px-7 py-5">
                <Badge className="mb-2 bg-emerald-100 text-emerald-700 border-emerald-200">{c.industry}</Badge>
                <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900">{c.title}</h2>
              </div>
              <div className="px-7 py-7 space-y-6">
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-2">
                    <Target className="w-4 h-4" /> Ausgangslage
                  </h3>
                  <p className="text-gray-700 leading-relaxed">{c.challenge}</p>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-2">
                    <Clock className="w-4 h-4" /> Vorgehen mit LaizyLeads
                  </h3>
                  <p className="text-gray-700 leading-relaxed">{c.approach}</p>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" /> Ergebnisse
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {c.result.map((r, idx) => (
                      <div key={idx} className="p-5 rounded-xl bg-gray-50 border border-gray-200 text-center">
                        <p className="text-3xl font-bold text-emerald-600 mb-1">{r.kpi}</p>
                        <p className="text-xs text-gray-600 leading-snug">{r.label}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <blockquote className="border-l-4 border-emerald-500 pl-5 py-2 bg-gray-50/50 rounded-r-md">
                  <Quote className="w-5 h-5 text-emerald-500 mb-2" />
                  <p className="text-gray-800 italic leading-relaxed">&bdquo;{c.quote}&ldquo;</p>
                </blockquote>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* INTERNAL LINKS */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-6">Ihr nächster Schritt</h2>
          <p className="text-gray-600 mb-8 max-w-2xl">
            Wir zeigen Ihnen gerne in einem kostenlosen Gespräch, wie LaizyLeads für <em>Ihre</em> Branche und <em>Ihren</em> ICP arbeiten würde.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <Link to="/leistungen" className="group p-6 rounded-2xl border border-gray-200 bg-white hover:border-emerald-300 hover:shadow-md transition-all">
              <h3 className="font-semibold mb-1 group-hover:text-emerald-700">So funktioniert der Leadgenerator</h3>
              <p className="text-sm text-gray-500">Datenbasierte Leadgenerierung Schritt für Schritt erklärt.</p>
            </Link>
            <Link to="/branchen" className="group p-6 rounded-2xl border border-gray-200 bg-white hover:border-emerald-300 hover:shadow-md transition-all">
              <h3 className="font-semibold mb-1 group-hover:text-emerald-700">Branchen & Use Cases</h3>
              <p className="text-sm text-gray-500">Welche Probleme wir in welcher Branche lösen.</p>
            </Link>
            <Link to="/kontakt" className="group p-6 rounded-2xl border border-gray-200 bg-white hover:border-emerald-300 hover:shadow-md transition-all">
              <h3 className="font-semibold mb-1 group-hover:text-emerald-700">Beratungsgespräch</h3>
              <p className="text-sm text-gray-500">Kostenlos & unverbindlich – wir prüfen Ihren Use Case.</p>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-600 to-emerald-700" />
        <div className="relative max-w-3xl mx-auto px-5 sm:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-4">Schreiben Sie Ihre eigene Case Study.</h2>
          <p className="text-emerald-100 text-base md:text-lg mb-8 max-w-xl mx-auto">
            Starten Sie jetzt mit LaizyLeads und erzielen Sie messbare Ergebnisse in den nächsten 90 Tagen.
          </p>
          <Button size="lg" className="bg-white hover:bg-gray-100 text-emerald-700 h-12 px-8 text-base shadow-xl font-semibold" onClick={() => navigate('/register')}>
            Jetzt kostenlos testen <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
